import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import LoadingSpinner from '@ui/loading-spinner/loading-spinner';
import { EToastTypes } from '@ui/toast/toast.types';

type TBlockNavOptions = {
  block?: boolean;
};

const useBlockNavigationWhileSavingProject = (options?: TBlockNavOptions) => {
  const toastId = useRef<string | number | null>(null);

  const isSavingProject = useAppSelector(selectIsSavingWorkingProject);

  // block navigation if the project is being saved, this prevents any potential data loss
  const blocker = useBlocker(options?.block ?? isSavingProject);

  // this useEffect will show a toast when the project is being saved, and then reset after the project is saved
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (isSavingProject) {
        toastId.current = toastTrigger('Hold tight while we finish saving your project...', {
          type: EToastTypes.info,
          autoClose: false,
          icon: <LoadingSpinner width={20} height={20} stroke={8} color="var(--gray-1200)" />,
        });
      }
      if (!isSavingProject) {
        blocker.reset();
      }
    }

    return () => {
      if (blocker.state === 'blocked') {
        blocker.reset();
      }
      if (toastId.current) {
        toast.dismiss(toastId.current);
        toastId.current = null;
      }
    };
    // intentionally removing isSavingProject from the dependency array or it will cause incorrect blocker state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker]);

  // this useEffect will handle proceeding with the intended navigation after the project is finished saved
  useEffect(() => {
    if (!isSavingProject) {
      if (blocker.state === 'blocked') {
        // this will continue the navigation after the project is saved
        blocker.proceed();

        // if the toast is still open, close it
        if (toastId.current) {
          toast.dismiss(toastId.current);
          toastId.current = null;
        }
      }
    }
    // intentionally removing blocker from the dependency array or it will cause incorrect blocker state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSavingProject]);

  // this useEffect will handle page refreshing while the project is being saved
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isSavingProject) return;

      event.preventDefault();
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [isSavingProject]);
};

export default useBlockNavigationWhileSavingProject;
